import React from 'react'
import { Input, InputAdornment } from '@mui/material'
import EmailIcon from '@mui/icons-material/Email'
import PersonIcon from '@mui/icons-material/Person'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'

export type TextInputFieldType = 'email' | 'username' | 'text'

export interface TextInputProps {
  id: string
  value: string
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  onFocus?: () => void
  onBlur?: () => void
  placeholder?: string
  autoComplete?: string
  disabled?: boolean
  required?: boolean
  hasError?: boolean
  errorId?: string
  errorMessage?: React.ReactNode
  helpText?: React.ReactNode
  type?: TextInputFieldType
  icon?: React.ReactNode
  className?: string
}

const DEFAULT_ICONS: Record<TextInputFieldType, React.ReactNode> = {
  email: <EmailIcon className="sevv-auth-input-icon-mui" />,
  username: <PersonIcon className="sevv-auth-input-icon-mui" />,
  text: null,
}

// Generic email/username/text sibling to PasswordInput. Kept structurally
// identical (same sevv-auth-input-* classes, same error/help markup) rather
// than sharing a base component with it, since the only real difference is
// the endAdornment (password's visibility toggle vs. no endAdornment here).
export default function TextInput({
  id,
  value,
  onChange,
  onFocus,
  onBlur,
  placeholder,
  autoComplete,
  disabled = false,
  required = false,
  hasError = false,
  errorId,
  errorMessage,
  helpText,
  type = 'text',
  icon,
  className = '',
}: TextInputProps) {
  const resolvedErrorId = errorId ?? `${id}-error`
  const showError = hasError && !!errorMessage
  const resolvedIcon = icon !== undefined ? icon : DEFAULT_ICONS[type]
  const nativeType = type === 'email' ? 'email' : 'text'

  return (
    <div className="sevv-auth-input-group">
      <Input
        id={id}
        className={`sevv-auth-input-mui ${hasError ? 'sevv-auth-input-error' : ''} ${className}`}
        disableUnderline
        fullWidth
        type={nativeType}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        onFocus={onFocus}
        onBlur={onBlur}
        autoComplete={autoComplete}
        disabled={disabled}
        required={required}
        inputProps={{
          'aria-invalid': hasError,
          'aria-describedby': showError ? resolvedErrorId : undefined,
        }}
        startAdornment={
          resolvedIcon ? <InputAdornment position="start">{resolvedIcon}</InputAdornment> : undefined
        }
      />
      {showError && (
        <div id={resolvedErrorId} className="sevv-auth-input-error-text">
          <ErrorOutlineIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
          {errorMessage}
        </div>
      )}
      {helpText && (
        <div className="sevv-auth-input-help">{helpText}</div>
      )}
    </div>
  )
}
